import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { apiService } from '../services/api';
import { DOCUMENTS_ENDPOINTS } from '../../api/endpoints';

export interface Document {
  id: string;
  name: string;
  path: string;
  folderPath: string;
  webUrl?: string;
  size?: number;
  lastModified?: string;
  isFolder: boolean;
  indexDepth: number;
}

export interface DocumentSearchResult {
  documents: Document[];
  count: number;
  searchTerm: string;
}

export interface DocumentIndexStatus {
  crisisId: string;
  documentCount: number;
  folderCount: number;
  lastIndexed?: string;
  hasIndex: boolean;
}

interface DocumentStore {
  // State
  documents: Document[];
  cachedSearches: Record<string, Document[]>;
  indexStatus: DocumentIndexStatus | null;
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  isFolderOnly: boolean;
  allDocumentsFetched: boolean;
  currentCrisisId: string | null;

  // Actions
  fetchAllDocuments: (crisisId: string) => Promise<void>;
  searchDocuments: (crisisId: string, searchTerm: string, isFolderOnly?: boolean) => Promise<void>;
  getDocumentsByFolder: (crisisId: string, folderPath: string, includeSubfolders?: boolean) => Promise<Document[]>;
  triggerIndexing: (crisisId: string) => Promise<{ job_id: string }>;
  triggerIndexingByFolders: (folderIds?: string[]) => Promise<{ job_id: string }>;
  getIndexStatus: (crisisId: string) => Promise<void>;
  clearCache: () => void;
  setError: (error: string | null) => void;
  setLoading: (isLoading: boolean) => void;
}

export const useDocumentStore = create<DocumentStore>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        documents: [],
        cachedSearches: {},
        indexStatus: null,
        isLoading: false,
        error: null,
        searchTerm: '',
        isFolderOnly: false,
        allDocumentsFetched: false,
        currentCrisisId: null,

        // Fetch all documents for a crisis (for instant client-side search)
        fetchAllDocuments: async (crisisId: string) => {
          // Only fetch if we haven't already fetched for this crisis
          if (get().allDocumentsFetched && get().currentCrisisId === crisisId) {
            return;
          }

          set({ isLoading: true, error: null, currentCrisisId: crisisId });

          try {
            const params = new URLSearchParams({
              search_term: '',
              is_folder_only: 'false',
              limit: '10000', // Fetch a very high number
            });

            const data = await apiService.get<{ data: DocumentSearchResult }>(
              `${DOCUMENTS_ENDPOINTS.SEARCH}?${params}`
            );

            set({
              documents: data.data.documents,
              allDocumentsFetched: true,
              isLoading: false,
            });
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Failed to fetch all documents';
            set({ error: errorMsg, isLoading: false });
          }
        },

        // Search documents by name/metadata (kept for compatibility)
        searchDocuments: async (crisisId: string, searchTerm: string, isFolderOnly = false) => {
          set({ isLoading: true, error: null, searchTerm, isFolderOnly });

          try {
            // Check cache first
            const cacheKey = `${crisisId}:${searchTerm}:${isFolderOnly}`;
            const cached = get().cachedSearches[cacheKey];
            if (cached && searchTerm.length > 0) {
              set({ documents: cached, isLoading: false });
              return;
            }

            const params = new URLSearchParams({
              search_term: searchTerm || '',
              is_folder_only: String(isFolderOnly),
              limit: '50',
            });

            const data = await apiService.get<{ data: DocumentSearchResult }>(
              `${DOCUMENTS_ENDPOINTS.SEARCH}?${params}`
            );

            const result = data.data;
            set((state) => ({
              documents: result.documents,
              cachedSearches: {
                ...state.cachedSearches,
                [cacheKey]: result.documents,
              },
              isLoading: false,
            }));
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Search failed';
            set({ error: errorMsg, isLoading: false, documents: [] });
          }
        },

        // Get documents by folder (from cached documents)
        getDocumentsByFolder: async (
          crisisId: string,
          folderPath: string,
        ): Promise<Document[]> => {
          // Ensure all documents are loaded
          if (!get().allDocumentsFetched) {
            await get().fetchAllDocuments(crisisId);
          }

          const allDocs = get().documents;

          // Filter documents at this folder level
          let filtered: Document[];

          if (folderPath === '' || folderPath === undefined) {
            // Root level - find the minimum depth (the topmost items)
            const minDepth = allDocs.reduce(
              (min, doc) => Math.min(min, doc.indexDepth),
              Infinity
            );
            filtered = allDocs.filter((doc) => doc.indexDepth === minDepth);
          } else {
            // Get items whose folderPath exactly matches this path (immediate children)
            filtered = allDocs.filter((doc) => doc.folderPath === folderPath);
          }

          return filtered;
        },

        // Trigger indexing job
        triggerIndexing: async () => {
          set({ isLoading: true, error: null });

          try {
            const result = await apiService.post<{ data: { job_id: string } }>(
              DOCUMENTS_ENDPOINTS.INDEX
            );
            set({ isLoading: false });
            return result.data;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Indexing failed';
            set({ error: errorMsg, isLoading: false });
            throw error;
          }
        },

        // Trigger indexing for specific folders
        triggerIndexingByFolders: async (folderIds?: string[]) => {
          set({ isLoading: true, error: null });

          try {
            const payload = folderIds ? { folder_ids: folderIds } : {};
            const result = await apiService.post<{ data: { job_id: string } }>(
              DOCUMENTS_ENDPOINTS.INDEX,
              payload
            );
            set({ isLoading: false });
            return result.data;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Indexing failed';
            set({ error: errorMsg, isLoading: false });
            throw error;
          }
        },

        // Get index status
        getIndexStatus: async (crisisId: string) => {
          try {
            const result = await apiService.get<{ data: DocumentIndexStatus }>(
              DOCUMENTS_ENDPOINTS.INDEX_STATUS(crisisId)
            );
            set({ indexStatus: result.data });
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Failed to get index status';
            set({ error: errorMsg });
          }
        },

        // Clear all caches
        clearCache: () => {
          set({
            documents: [],
            cachedSearches: {},
            indexStatus: null,
            searchTerm: '',
            isFolderOnly: false,
            allDocumentsFetched: false,
            currentCrisisId: null,
          });
        },

        // Set error
        setError: (error: string | null) => {
          set({ error });
        },

        // Set loading
        setLoading: (isLoading: boolean) => {
          set({ isLoading });
        },
      }),
      {
        name: 'document-store',
        partialize: (state) => ({
          cachedSearches: state.cachedSearches,
          indexStatus: state.indexStatus,
        }),
      }
    )
  )
);
